import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { canManagePlatformSettings } from '../utils/platform-settings';

export interface NavSection {
  id: string;
  label: string;
  permission: string;
  icon: string;
  feature?: string;
}

export const sections: NavSection[] = [
  { id: 'overview', label: 'nav.overview', permission: 'metric:read', icon: 'dashboard' },
  { id: 'metrics', label: 'nav.metrics', permission: 'metric:read', icon: 'view-list' },
  { id: 'metric-import', label: 'nav.metric-import', permission: 'metric:write', icon: 'file-import' },
  { id: 'query', label: 'nav.query', permission: 'query:read', icon: 'chat' },
  { id: 'governance', label: 'nav.governance', permission: 'metric:read', icon: 'verify' },
  { id: 'metadata', label: 'nav.metadata', permission: 'metadata:read', icon: 'file-import' },
  { id: 'knowledge', label: 'nav.knowledge', permission: 'knowledge:read', icon: 'file' },
  { id: 'targets', label: 'nav.targets', permission: 'target:read', icon: 'flag' },
  { id: 'tasks', label: 'nav.tasks', permission: 'task:read', icon: 'task' },
  { id: 'dashboards', label: 'nav.dashboards', permission: 'dashboard:read', icon: 'dashboard', feature:'dashboard_designer' },
  { id: 'dashboard-datasets', label: 'nav.dashboardDatasets', permission: 'dashboard:read', icon: 'database', feature:'dashboard_designer' },
  { id: 'sources', label: 'nav.sources', permission: 'source:read', icon: 'server' },
  { id: 'dw-builder', label: 'nav.dw-builder', permission: 'source:read', icon: 'server', feature:'dynamic_dw_builder' },
  { id: 'business-objects', label: 'nav.business-objects', permission: 'business_object:read', icon: 'view-list', feature:'business_objects' },
  { id: 'exports', label: 'nav.exports', permission: 'export:write', icon: 'download' },
  { id: 'users', label: 'nav.users', permission: 'user:write', icon: 'user' },
  { id: 'dingtalk', label: 'nav.dingtalk', permission: 'user:write', icon: 'user' },
  { id: 'platform-settings', label: 'nav.platformSettings', permission: 'policy:write', icon: 'server' },
  { id: 'roles', label: 'nav.roles', permission: 'policy:read', icon: 'user-circle' },
  { id: 'audit', label: 'nav.audit', permission: 'audit:read', icon: 'secured' },
];

const routes = [
  { path: '/login', name: 'login', component: () => import('../views/LoginView.vue') },
  { path: '/auth/callback', name: 'callback', component: () => import('../views/LoginView.vue') },
  { path: '/unavailable', name: 'unavailable', component: () => import('../views/RouteStateView.vue') },
  { path: '/present/dashboards/:id', name: 'dashboard-presentation', component: () => import('../views/DashboardPresentationView.vue'), meta: { permission: 'dashboard:read', feature: 'dashboard_designer', presentation: true } },
  { path: '/present/dashboards/by-slug/:slug', name: 'dashboard-presentation-slug', component: () => import('../views/DashboardPresentationView.vue'), meta: { permission: 'dashboard:read', feature: 'dashboard_designer', presentation: true } },
  {
    path: '/',
    component: () => import('../components/layout/AppLayout.vue'),
    children: [
      { path: '', name: 'overview', component: () => import('../views/OverviewView.vue'), meta: { permission: 'metric:read' } },
      { path: 'metrics', name: 'metrics', component: () => import('../views/MetricsView.vue'), meta: { permission: 'metric:read' } },
      { path: 'metrics/import', name: 'metric-import', component: () => import('../views/MetricImportView.vue'), meta: { permission: 'metric:write' } },
      { path: 'query', name: 'query', component: () => import('../views/QueryView.vue'), meta: { permission: 'query:read' } },
      { path: 'governance', name: 'governance', component: () => import('../views/GovernanceView.vue'), meta: { permission: 'metric:read' } },
      { path: 'metadata', name: 'metadata', component: () => import('../views/MetadataView.vue'), meta: { permission: 'metadata:read' } },
      { path: 'knowledge', name: 'knowledge', component: () => import('../views/KnowledgeView.vue'), meta: { permission: 'knowledge:read' } },
      { path: 'targets', name: 'targets', component: () => import('../views/TargetsView.vue'), meta: { permission: 'target:read' } },
      { path: 'tasks', name: 'tasks', component: () => import('../views/TasksView.vue'), meta: { permission: 'task:read' } },
      { path: 'dashboards', name: 'dashboards', component: () => import('../views/DashboardsView.vue'), meta: { permission: 'dashboard:read', feature:'dashboard_designer' } },
      { path: 'dashboard-datasets', name: 'dashboard-datasets', component: () => import('../views/DashboardDatasetsView.vue'), meta: { permission: 'dashboard:read', feature:'dashboard_designer' } },
      { path: 'dashboards/:id/view', name: 'dashboard-viewer', component: () => import('../views/DashboardViewerView.vue'), meta: { permission: 'dashboard:read', feature:'dashboard_designer' } },
      { path: 'dashboards/by-slug/:slug/view', name: 'dashboard-viewer-slug', component: () => import('../views/DashboardViewerView.vue'), meta: { permission: 'dashboard:read', feature:'dashboard_designer' } },
      { path: 'sources', name: 'sources', component: () => import('../views/SourcesView.vue'), meta: { permission: 'source:read' } },
      { path: 'dw-builder', name: 'dw-builder', component: () => import('../views/DwBuilderView.vue'), meta: { permission: 'source:read' } },
      { path: 'business-objects', name: 'business-objects', component: () => import('../views/BusinessObjectsView.vue'), meta: { permission: 'business_object:read', feature: 'business_objects' } },
      { path: 'business-objects/models', name: 'business-object-models', component: () => import('../views/BusinessObjectModelsView.vue'), meta: { permission: 'business_object:read', feature: 'business_objects' } },
      { path: 'business-objects/instances/:id', name: 'business-object-detail', component: () => import('../views/BusinessObjectDetailView.vue'), meta: { permission: 'business_object:read', feature: 'business_objects' } },
      { path: 'business-objects/dw-preview', name: 'business-object-dw-preview', component: () => import('../views/BusinessObjectDwView.vue'), meta: { permission: 'business_object:dw', feature: 'business_objects_dw' } },
      { path: 'exports', name: 'exports', component: () => import('../views/ExportsView.vue'), meta: { permission: 'export:write' } },
      { path: 'users', name: 'users', component: () => import('../views/UsersView.vue'), meta: { permission: 'user:write' } },
      { path: 'dingtalk', name: 'dingtalk', component: () => import('../views/DingTalkAdminView.vue'), meta: { permission: 'user:write' } },
      { path: 'settings/:namespace?', name: 'platform-settings', component: () => import('../views/PlatformSettingsView.vue'), meta: { platformSettings: true } },
      { path: 'roles', name: 'roles', component: () => import('../views/RolesView.vue'), meta: { permission: 'policy:read' } },
      { path: 'audit', name: 'audit', component: () => import('../views/AuditView.vue'), meta: { permission: 'audit:read' } },
      { path: 'forbidden', name: 'forbidden', component: () => import('../views/RouteStateView.vue') },
      { path: ':pathMatch(.*)*', name: 'not-found', component: () => import('../views/RouteStateView.vue') },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to) => {
  if (to.query.code && to.query.state && to.name !== 'callback') {
    return { name: 'callback', query: to.query, replace: true };
  }
  if (['login', 'callback', 'unavailable'].includes(String(to.name))) return true;
  const auth = useAuthStore();
  if (!auth.token) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  if (!auth.me) {
    try { await auth.fetchMe(); } catch { return { name: 'unavailable' }; }
  }
  if (!auth.isAuthed) return { name: 'login', query: { redirect: to.fullPath } };
  if (to.meta.platformSettings && !canManagePlatformSettings(auth.me)) return { name: 'forbidden' };
  if (to.meta.permission && !auth.permitted(to.meta.permission as string)) {
    const first = sections.find(section => auth.permitted(section.permission));
    return to.name === 'overview' && first ? { name: first.id } : { name: 'forbidden' };
  }
  if (to.meta.feature) {
    try {
      const flags = await auth.api<Record<string, boolean>>('/features');
      if (!flags[to.meta.feature as string]) return { name: 'unavailable', query: { reason: 'feature-disabled' } };
    } catch { return { name: 'unavailable' }; }
  }
  return true;
});

window.addEventListener('compass:session-expired', () => {
  router.replace({ name: 'login', query: { redirect: router.currentRoute.value.fullPath } });
});

export default router;
